import { useId, useMemo } from 'react';
import { cn } from '@/lib/cn';
import horizontalRaw from '@/assets/brand/logo-horizontal.svg?raw';
import stackedRaw from '@/assets/brand/logo-stacked.svg?raw';
import markRaw from '@/assets/brand/logo-mark.svg?raw';

type Lockup = 'horizontal' | 'stacked' | 'mark';

const SOURCES: Record<Lockup, string> = {
  horizontal: horizontalRaw,
  stacked: stackedRaw,
  mark: markRaw,
};

interface LogoProps {
  lockup?: Lockup | undefined;
  className?: string | undefined;
  title?: string | undefined;
}

export function Logo({
  lockup = 'horizontal',
  className,
  title = 'MusicVille Academy',
}: LogoProps): React.JSX.Element {
  const uid = useId().replace(/:/g, '');

  const markup = useMemo(() => {
    const scoped = `mv-${uid}`;
    return SOURCES[lockup]
      .replace(/id="mv"/g, `id="${scoped}"`)
      .replace(/url\(#mv\)/g, `url(#${scoped})`)
      .replace(
        /^(<svg\b[^>]*?)>/,
        (_match, openTag: string) => `${openTag.replace(/\s(?:width|height)="[\d.]+"/g, '')}>`,
      )
      .replace(/<title>[^<]*<\/title>/, `<title>${title}</title>`);
  }, [lockup, uid, title]);

  return (
    <span
      className={cn(
        'inline-block [&>svg]:block [&>svg]:h-full [&>svg]:w-auto',
        lockup === 'horizontal' && 'h-9',
        lockup === 'stacked' && 'h-28',
        lockup === 'mark' && 'h-9',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
